import {
  Box,
  TableContainer,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from "@mui/material";
import { Fragment, FunctionComponent, ReactNode, useState } from "react";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import { get, isEmpty } from "lodash";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { getShortId } from "../../utils/commonHelpers";
import "./style.scss";

export interface IColumn {
  name: string;
  valueOrProcessor: string | ((item: any) => string | ReactNode);
  formatter?: "date" | "shortId";
  align?: "left" | "right" | "center";
}

export interface TableConfig {
  rowIdentifierKey: string;
  columns: IColumn[];
  data: any[];
  subConfig?: Omit<TableConfig, "data"> & { dataKey: string };
  backgroundColor?: string;
  noHeadings?: boolean;
  height?: number;
}

const Table: FunctionComponent<TableConfig> = ({
  rowIdentifierKey,
  columns,
  data,
  subConfig,
  backgroundColor,
  noHeadings = false,
  height,
}) => {
  const [isRowExpanded, setIsRowExpanded] = useState<{
    [key: string]: boolean;
  }>({});

  return (
    <TableContainer
      className="common-component-table"
      component={Box}
      style={{ backgroundColor, height }}
    >
      <MuiTable>
        {!noHeadings && (
          <TableHead>
            <TableRow>
              {columns.map(({ name, align }) => (
                <TableCell
                  key={name}
                  align={align}
                  className="th-cell"
                  sx={{
                    padding: 1,
                    "&:first-child": subConfig ? { paddingLeft: 4 } : {},
                  }}
                >
                  <strong>{name}</strong>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {isEmpty(data) ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Typography variant="body2" color="textSecondary">
                  Nothing to show. No Data.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, dataIndex) => (
              <Fragment key={item[rowIdentifierKey]}>
                <TableRow
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  {columns.map(
                    (
                      { name, valueOrProcessor, formatter, align },
                      columnIndex,
                    ) => {
                      let value =
                        typeof valueOrProcessor === "string"
                          ? get(item, valueOrProcessor)
                          : valueOrProcessor(item);

                      if (formatter === "date") {
                        value = value && moment(value).format(DATE_TIME_FORMAT);
                      } else if (formatter === "shortId") {
                        value = getShortId(value);
                      }

                      return (
                        <TableCell
                          key={name}
                          align={align}
                          sx={{
                            padding: 1,
                            backgroundColor: isRowExpanded[
                              item[rowIdentifierKey]
                            ]
                              ? "#e6e6e6"
                              : "none",
                          }}
                        >
                          <Box
                            display={"flex"}
                            flexDirection={"row"}
                            alignItems={"center"}
                          >
                            {!!subConfig &&
                              columnIndex === 0 &&
                              (isRowExpanded[item[rowIdentifierKey]] ? (
                                <KeyboardArrowDown
                                  onClick={() =>
                                    setIsRowExpanded((prevState) => ({
                                      ...prevState,
                                      [item[rowIdentifierKey]]: false,
                                    }))
                                  }
                                />
                              ) : (
                                <KeyboardArrowRight
                                  onClick={() =>
                                    setIsRowExpanded((prevState) => ({
                                      ...prevState,
                                      [item[rowIdentifierKey]]: true,
                                    }))
                                  }
                                />
                              ))}
                            {value}
                          </Box>
                        </TableCell>
                      );
                    },
                  )}
                </TableRow>

                <TableRow
                  key={`${item.id}_${dataIndex}_subconfig`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  {subConfig && !!isRowExpanded[item[rowIdentifierKey]] ? (
                    <TableCell
                      colSpan={columns.length}
                      sx={{
                        paddingTop: 0,
                        paddingBottom: 0,
                      }}
                    >
                      <Table data={item[subConfig.dataKey]} {...subConfig} />
                    </TableCell>
                  ) : null}
                </TableRow>
              </Fragment>
            ))
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
